import React, { useState, useEffect, useRef, useMemo } from "react";
import "./Catalogue.css";
import { db } from "../services/firebase";
import {
  collection,
  getDocs,
  query,
  doc,
  updateDoc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { useSeller } from "../contexts/SellerContext";
import sellerProductData, {
  getCategoriesForSegment,
  getSubcategoriesForSegment,
  getProductOptions,
} from "./SellerSegment";
import { useNavigate } from "react-router-dom";
import "../pages/LotDrawer.css";
import EditRejectedProductModal from "../components/EditRejectedProductModal";

const Catalogue = () => {
  const { type } = useParams();
  const { seller } = useSeller();
  const sellerId = seller?.sellerId;
  const Segment = seller?.segment?.toUpperCase() || "";
  const navigate = useNavigate();

  // State Management
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [subcategoryCounts, setSubcategoryCounts] = useState({});

  // Drawer States
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editRejectedModalOpen, setEditRejectedModalOpen] = useState(false);
  const [selectedRejectedProduct, setSelectedRejectedProduct] = useState(null);
  const [lotSizes, setLotSizes] = useState([
    {
      size: "",
      set: "",
      grossWt: "",
      netWt: "",
      avgGrossWt: "",
      avgNetWt: "",
      avgSpecWt: "",
    },
  ]);
  const sizeRowRefs = useRef({});

  // Categories & Subcategories Filter States
  const categories = useMemo(
    () => (Segment ? getCategoriesForSegment(Segment) : []),
    [Segment],
  );
  const [selectedCategory, setSelectedCategory] = useState("");

  const subcategories = useMemo(
    () =>
      Segment ? getSubcategoriesForSegment(Segment, selectedCategory) : [],
    [Segment, selectedCategory],
  );
  const [selectedSubcategory, setSelectedSubcategory] = useState("");

  useEffect(() => {
    if (
      categories.length > 0 &&
      (!selectedCategory || !categories.includes(selectedCategory))
    ) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (
      subcategories.length > 0 &&
      (!selectedSubcategory || !subcategories.includes(selectedSubcategory))
    ) {
      setSelectedSubcategory(subcategories[0]);
    }
  }, [subcategories, selectedSubcategory]);

  // Configuration
  const config = {
    pending: {
      title: "QC Pending",
      filterStatus: "pending",
      badgeText: "QC Pending",
      badgeColor: "rgba(245, 158, 11, 0.15)",
      badgeBorderColor: "rgba(245, 158, 11, 0.45)",
      badgeTextColor: "#d97706",
      actionButtons: [],
    },
    rejected: {
      title: "QC Rejected",
      filterStatus: "rejected",
      badgeText: "QC Rejected",
      badgeColor: "rgba(239, 68, 68, 0.15)",
      badgeBorderColor: "rgba(239, 68, 68, 0.45)",
      badgeTextColor: "#dc2626",
      actionButtons: [
        {
          text: "Delete",
          color: "#ef4444",
          onClick: (productOrId) => handleDeleteRejectedProduct(productOrId),
        },
        {
          text: "Edit",
          color: "#c49b27",
          onClick: (product) => openEditRejectedModal(product),
        },
      ],
    },
    ready: {
      title: "Ready Stock Catalogue",
      filterStatus: "approved",
      serviceType: "ready",
      badgeText: "Ready Stock",
      badgeColor: "rgba(16, 185, 129, 0.15)",
      badgeBorderColor: "rgba(16, 185, 129, 0.45)",
      badgeTextColor: "#059669",
      actionButtons: [
        {
          text: "StockOut",
          color: "#ef4444",
          onClick: (productId) => handleMarkOutOfStock(productId),
        },
        {
          text: "Order",
          color: "#f97316",
          onClick: (productId) => handleMarkOrderServe(productId),
        },
      ],
    },
    order: {
      title: "Order Serve Catalogue",
      filterStatus: "approved",
      serviceType: "order",
      badgeText: "Order Serve",
      badgeColor: "rgba(249, 115, 22, 0.15)",
      badgeBorderColor: "rgba(249, 115, 22, 0.45)",
      badgeTextColor: "#ea580c",
      actionButtons: [
        {
          text: "Ready",
          color: "#10b981",
          onClick: (product) => openEditDrawer(product),
        },
        {
          text: "StockOut",
          color: "#ef4444",
          onClick: (productId) => handleMarkOutOfStock(productId),
        },
      ],
    },
    out: {
      title: "Out of Stock Catalogue",
      filterStatus: "approved",
      serviceType: "out",
      badgeText: "Out of Stock",
      badgeColor: "rgba(107, 114, 128, 0.15)",
      badgeBorderColor: "rgba(107, 114, 128, 0.45)",
      badgeTextColor: "#4b5563",
      actionButtons: [
        {
          text: "Ready",
          color: "#10b981",
          onClick: (product) => openEditDrawer(product),
        },
        {
          text: "Order",
          color: "#f97316",
          onClick: (productId) => handleMarkOrderServe(productId),
        },
      ],
    },
  };

  const currentConfig = config[type] || config["pending"];

  // ========== LOT/SIZE-SPECIFIC FUNCTIONS ==========

  const getProductDisplayInfo = (product) => {
    if (product.lotDetails && product.lotDetails.length > 0) {
      const totalSets =
        product.lotTotals?.totalSets ??
        product.lotDetails.reduce(
          (sum, item) => sum + (parseFloat(item.set) || 0),
          0,
        );
      const totalNetWt =
        product.lotTotals?.totalNetWt ??
        product.lotDetails.reduce(
          (sum, item) => sum + (parseFloat(item.netWt) || 0),
          0,
        );
      const totalGrossWt =
        product.lotTotals?.totalGrossWt ??
        product.lotDetails.reduce(
          (sum, item) => sum + (parseFloat(item.grossWt) || 0),
          0,
        );

      const sizes = product.lotDetails.map((item) => item.size).filter(Boolean);
      const uniqueSizes = [...new Set(sizes)];

      return {
        displayWeight: `${totalNetWt.toFixed(3)}g`,
        displaySets: `${totalSets} sets`,
        displaySizes: uniqueSizes.join(", "),
        isLot: true,
        totalSets,
        totalNetWt,
        totalGrossWt,
      };
    } else {
      return {
        displayWeight: `${product.netWt || 0}g`,
        displaySets: product.instockSet ? `${product.instockSet} sets` : "",
        displaySizes: Array.isArray(product.sizes)
          ? product.sizes.join(", ")
          : product.sizes || "",
        isLot: false,
      };
    }
  };

  const openEditDrawer = (product) => {
    setEditingProduct(product);
    const existingDetails = product.lotDetails;

    if (existingDetails && existingDetails.length > 0) {
      // Reset set count to "0" for each size detail when moving/updating product to ready stock
      const initialSizes = existingDetails.map((item) => ({
        size: item.size || "",
        set: "0",
        grossWt: item.grossWt?.toString() || "",
        netWt: item.netWt?.toString() || "",
        avgGrossWt: item.avgGrossWt?.toString() || "",
        avgNetWt: item.avgNetWt?.toString() || "",
        avgSpecWt: item.avgSpecWt?.toString() || "",
      }));
      setLotSizes(initialSizes);
    } else {
      const defaultSize = Array.isArray(product.sizes)
        ? product.sizes[0] || ""
        : product.sizes || "";
      setLotSizes([
        {
          size: defaultSize,
          set: "0",
          grossWt: product.grossWt?.toString() || "",
          netWt: product.netWt?.toString() || "",
          avgGrossWt: "",
          avgNetWt: "",
          avgSpecWt: "",
        },
      ]);
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
    setLotSizes([
      {
        size: "",
        set: "",
        grossWt: "",
        netWt: "",
        avgGrossWt: "",
        avgNetWt: "",
        avgSpecWt: "",
      },
    ]);
  };

  const getSizeOptions = () => {
    if (!editingProduct) return [];

    const segment = editingProduct.segment?.toUpperCase();
    const category = editingProduct.category;
    const source = editingProduct.productSource;
    const productName = editingProduct.productName?.toUpperCase();

    let options = getProductOptions(segment, category, source, productName);

    const existingInLot = lotSizes.map((item) => item.size).filter(Boolean);
    const existingInProduct = Array.isArray(editingProduct.sizes)
      ? editingProduct.sizes
      : typeof editingProduct.sizes === "string"
      ? editingProduct.sizes.split(",").map((s) => s.trim())
      : [];

    const combined = Array.from(
      new Set([...options, ...existingInLot, ...existingInProduct]),
    ).filter(Boolean);
    return combined;
  };

  const calculateItemAverages = (item) => {
    const set = parseFloat(item.set) || 0;
    const grossWt = parseFloat(item.grossWt) || 0;
    const netWt = parseFloat(item.netWt) || 0;
    const spec = editingProduct?.specification || "PLANE";

    if (set > 0) {
      const specWt = spec !== "PLANE" ? Math.max(0, grossWt - netWt) : 0;
      return {
        avgGrossWt: (grossWt / set).toFixed(3),
        avgNetWt: (netWt / set).toFixed(3),
        avgSpecWt: (specWt / set).toFixed(3),
      };
    } else {
      return {
        avgGrossWt:
          item.avgGrossWt || (grossWt > 0 ? grossWt.toFixed(3) : "0.000"),
        avgNetWt: item.avgNetWt || (netWt > 0 ? netWt.toFixed(3) : "0.000"),
        avgSpecWt: item.avgSpecWt || "0.000",
      };
    }
  };

  const calculateTotals = () => {
    return {
      totalSets: lotSizes.reduce(
        (sum, item) => sum + (parseFloat(item.set) || 0),
        0,
      ),
      totalGrossWt: lotSizes.reduce(
        (sum, item) => sum + (parseFloat(item.grossWt) || 0),
        0,
      ),
      totalNetWt: lotSizes.reduce(
        (sum, item) => sum + (parseFloat(item.netWt) || 0),
        0,
      ),
    };
  };

  const setSizeRowRef = (index, element) => {
    if (element) {
      sizeRowRefs.current[index] = element;
    }
  };

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...lotSizes];
    const updatedItem = { ...newSizes[index], [field]: value };

    if (field === "grossWt" || field === "netWt") {
      const gross = parseFloat(
        field === "grossWt" ? value : updatedItem.grossWt,
      );
      const net = parseFloat(field === "netWt" ? value : updatedItem.netWt);
      if (!isNaN(gross) && !isNaN(net) && gross > 0 && net > gross) {
        alert("Net Weight cannot be greater than Gross Weight.");
        return;
      }
    }

    newSizes[index] = updatedItem;

    if (field === "set" || field === "grossWt" || field === "netWt") {
      const averages = calculateItemAverages(newSizes[index]);
      newSizes[index] = {
        ...newSizes[index],
        avgGrossWt: averages.avgGrossWt,
        avgNetWt: averages.avgNetWt,
        avgSpecWt: averages.avgSpecWt,
      };
    }

    setLotSizes(newSizes);
  };

  const handleSaveData = async () => {
    if (!editingProduct) return;

    const hasZeroSet = lotSizes.some(
      (item) => item.set === "0" || item.set === 0,
    );
    if (hasZeroSet) {
      alert(
        "Set count cannot be 0. Please enter a valid set count before saving.",
      );
      return;
    }

    for (let i = 0; i < lotSizes.length; i++) {
      const item = lotSizes[i];
      const gross = parseFloat(item.grossWt) || 0;
      const net = parseFloat(item.netWt) || 0;
      if (net > gross) {
        alert(
          `Net weight (${item.netWt}g) cannot be greater than Gross weight (${
            item.grossWt
          }g) for ${item.size ? "size " + item.size : "row " + (i + 1)}.`,
        );
        return;
      }
    }

    try {
      const isPlane = editingProduct.specification === "PLANE";
      const productRef = doc(db, "products", editingProduct.id);

      const updatedLotSizes = lotSizes.map((item) => {
        const set = parseFloat(item.set) || 0;
        const gross = parseFloat(item.grossWt) || 0;
        const net = parseFloat(item.netWt) || 0;
        return {
          size: item.size || "",
          set:
            item.set !== undefined && item.set !== null
              ? item.set.toString()
              : "0",
          grossWt: item.grossWt?.toString() || "0",
          netWt: item.netWt?.toString() || "0",
          avgGrossWt:
            set > 0 ? (gross / set).toFixed(3) : item.avgGrossWt || "0.000",
          avgNetWt: set > 0 ? (net / set).toFixed(3) : item.avgNetWt || "0.000",
          avgSpecWt: !isPlane
            ? set > 0
              ? ((gross - net) / set).toFixed(3)
              : item.avgSpecWt || "0.000"
            : "0.000",
        };
      });

      const totals = calculateTotals();
      const newSizes = updatedLotSizes.map((item) => item.size).filter(Boolean);
      const specWt = !isPlane
        ? (totals.totalGrossWt - totals.totalNetWt).toFixed(3)
        : "0";

      const updatePayload = {
        serviceType: "ready",
        lotDetails: updatedLotSizes,
        lotTotals: totals,
        grossWt: totals.totalGrossWt.toFixed(3),
        netWt: totals.totalNetWt.toFixed(3),
        instockGram: totals.totalGrossWt.toFixed(3),
        instockSet: totals.totalSets.toString(),
        sizes: newSizes,
        specificationWt: specWt,
      };

      await updateDoc(productRef, updatePayload);

      setAllProducts((prev) =>
        prev.map((product) =>
          product.id === editingProduct.id
            ? {
                ...product,
                ...updatePayload,
              }
            : product,
        ),
      );
      alert("Product updated and moved to Ready Stock successfully!");
      closeDrawer();
    } catch (error) {
      console.error("Error updating stock details:", error);
      alert("Error updating stock details. Please try again.");
    }
  };

  const handleMarkOutOfStock = async (productId) => {
    try {
      const productRef = doc(db, "products", productId);
      const currentProduct = allProducts.find((p) => p.id === productId);
      const updatedLotDetails = (currentProduct?.lotDetails || []).map(
        (item) => ({
          ...item,
          set: "0",
        }),
      );

      await updateDoc(productRef, {
        instockGram: 0,
        instockSet: 0,
        lotDetails: updatedLotDetails,
        serviceType: "out",
      });

      setAllProducts((prev) =>
        prev.map((product) =>
          product.id === productId
            ? {
                ...product,
                instockGram: 0,
                instockSet: 0,
                lotDetails: updatedLotDetails,
                serviceType: "out",
              }
            : product,
        ),
      );

      alert("Product marked as Out of Stock successfully!");
    } catch (error) {
      console.error("Error marking product as Out of Stock:", error);
      alert("Error marking product as Out of Stock. Please try again.");
    }
  };

  const handleMarkOrderServe = async (productId) => {
    try {
      const productRef = doc(db, "products", productId);
      const currentProduct = allProducts.find((p) => p.id === productId);

      if (!currentProduct) {
        alert("Product not found");
        return;
      }

      const currentDetails = currentProduct.lotDetails || [];

      const resetDetails = currentDetails.map((item) => ({
        set: "0",
        size: item.size || "",
        grossWt: item.grossWt?.toString() || "",
        netWt: item.netWt?.toString() || "",
        avgGrossWt: item.avgGrossWt?.toString() || "",
        avgNetWt: item.avgNetWt?.toString() || "",
        avgSpecWt: item.avgSpecWt?.toString() || "",
      }));

      await updateDoc(productRef, {
        instockGram: 0,
        instockSet: 0,
        serviceType: "order",
        lotDetails: resetDetails,
      });

      setAllProducts((prev) =>
        prev.map((product) =>
          product.id === productId
            ? {
                ...product,
                instockGram: 0,
                instockSet: 0,
                serviceType: "order",
                lotDetails: resetDetails,
              }
            : product,
        ),
      );

      alert("Product moved to Order Serve successfully!");
    } catch (error) {
      console.error("Error marking product as Order Serve:", error);
      alert("Error marking product as Order Serve. Please try again.");
    }
  };

  const openEditRejectedModal = (product) => {
    setSelectedRejectedProduct(product);
    setEditRejectedModalOpen(true);
  };

  const handleProductUpdated = (updatedProduct) => {
    setAllProducts((prev) =>
      prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p)),
    );
  };

  const handleProductDeletedFromModal = (productId) => {
    setAllProducts((prev) => prev.filter((p) => p.id !== productId));
    setProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleDeleteRejectedProduct = async (productOrId) => {
    const productId =
      typeof productOrId === "object" ? productOrId.id : productOrId;
    const prod = allProducts.find((p) => p.id === productId);
    const prodName = prod?.productName || "this product";

    if (
      !window.confirm(
        `Are you sure you want to delete "${prodName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      await deleteDoc(doc(db, "products", productId));
      setAllProducts((prev) => prev.filter((p) => p.id !== productId));
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      alert("Product deleted successfully.");
    } catch (error) {
      console.error("Error deleting product:", error);
      alert("Failed to delete product: " + error.message);
    }
  };

  const handleProductClick = (product) => {
    if (
      product.status === "rejected" ||
      currentConfig.filterStatus === "rejected"
    ) {
      openEditRejectedModal(product);
    } else {
      navigate(`/product/${product.id}`, { state: { product } });
    }
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleSubcategoryClick = (subcategory) => {
    setSelectedSubcategory(subcategory);
  };

  useEffect(() => {
    const fetchAllProducts = async () => {
      if (!sellerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const productsRef = collection(db, "products");
        const q = query(productsRef, where("sellerId", "==", sellerId));
        const querySnapshot = await getDocs(q);

        const productsList = [];
        querySnapshot.forEach((doc) => {
          productsList.push({ id: doc.id, ...doc.data() });
        });

        // Client-side sort descending by timestamp to avoid requiring composite Firestore indexes
        productsList.sort((a, b) => {
          const ta =
            a.timestamp?.seconds ||
            (a.timestamp ? new Date(a.timestamp).getTime() / 1000 : 0);
          const tb =
            b.timestamp?.seconds ||
            (b.timestamp ? new Date(b.timestamp).getTime() / 1000 : 0);
          return tb - ta;
        });

        setAllProducts(productsList);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    };

    fetchAllProducts();
  }, [sellerId]);

  useEffect(() => {
    if (!sellerId) {
      setProducts([]);
      return;
    }

    if (allProducts.length === 0) {
      setProducts([]);
      setCategoryCounts({});
      setSubcategoryCounts({});
      return;
    }

    const productsForCounts = allProducts.filter((product) => {
      if (product.sellerId !== sellerId) return false;
      if (
        currentConfig.filterStatus === "pending" ||
        currentConfig.filterStatus === "rejected"
      ) {
        if (product.status !== currentConfig.filterStatus) return false;
      } else {
        if (
          product.status !== "approved" ||
          product.serviceType !== currentConfig.serviceType
        )
          return false;
      }
      return true;
    });

    const catCounts = {};
    const subCounts = {};
    productsForCounts.forEach((p) => {
      const cat = p.category || "All";
      const sub = p.productSource || "All";
      catCounts[cat] = (catCounts[cat] || 0) + 1;
      subCounts[sub] = (subCounts[sub] || 0) + 1;
    });
    setCategoryCounts(catCounts);
    setSubcategoryCounts(subCounts);

    // Auto-select category/subcategory that has products if current selection has 0 items
    if (productsForCounts.length > 0) {
      if (!selectedCategory || !catCounts[selectedCategory]) {
        const firstCatWithItems = categories.find((c) => catCounts[c] > 0);
        if (firstCatWithItems && firstCatWithItems !== selectedCategory) {
          setSelectedCategory(firstCatWithItems);
          return;
        }
      }
      if (!selectedSubcategory || !subCounts[selectedSubcategory]) {
        const firstSubWithItems = subcategories.find((s) => subCounts[s] > 0);
        if (firstSubWithItems && firstSubWithItems !== selectedSubcategory) {
          setSelectedSubcategory(firstSubWithItems);
          return;
        }
      }
    }

    const filteredProducts = productsForCounts.filter((product) => {
      if (selectedCategory && product.category !== selectedCategory)
        return false;
      if (selectedSubcategory && product.productSource !== selectedSubcategory)
        return false;

      return true;
    });

    setProducts(filteredProducts);
  }, [
    allProducts,
    sellerId,
    type,
    currentConfig.filterStatus,
    currentConfig.serviceType,
    selectedCategory,
    selectedSubcategory,
    categories,
    subcategories,
  ]);

  // ========== RENDER COMPONENTS ==========

  const renderLotDrawerContent = () => {
    const totals = calculateTotals();

    return (
      <>
        <div className="size-input-section">
          <h4>
            {editingProduct.productName} - {editingProduct.specification}
          </h4>
          {lotSizes.map((item, index) => (
            <div
              key={index}
              className="size-row"
              ref={(el) => setSizeRowRef(index, el)}
              data-row-index={index}
            >
              <div className="form-group">
                <label>Size</label>
                <select
                  value={item.size}
                  onChange={(e) =>
                    handleSizeChange(index, "size", e.target.value)
                  }
                  data-row-index={index}
                >
                  <option value="">Select Size</option>
                  {getSizeOptions().map((size) => (
                    <option
                      key={size}
                      value={size}
                      disabled={lotSizes.some(
                        (item, i) => i !== index && item.size === size,
                      )}
                    >
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Set</label>
                <input
                  type="number"
                  value={item.set}
                  onChange={(e) =>
                    handleSizeChange(index, "set", e.target.value)
                  }
                  data-row-index={index}
                />
              </div>

              <div className="form-group">
                <label>Gross Wt. (g)</label>
                <input
                  type="number"
                  step="0.001"
                  value={item.grossWt}
                  onChange={(e) =>
                    handleSizeChange(index, "grossWt", e.target.value)
                  }
                  data-row-index={index}
                />
              </div>

              <div className="form-group">
                <label>Net Wt. (g)</label>
                <input
                  type="number"
                  step="0.001"
                  value={item.netWt}
                  onChange={(e) =>
                    handleSizeChange(index, "netWt", e.target.value)
                  }
                  data-row-index={index}
                />
              </div>

              {/* Display calculated averages */}
              {(item.avgGrossWt || item.avgNetWt || item.avgSpecWt) && (
                <div className="calculated-averages">
                  <div className="avg-item">
                    <span>Avg Gross: {item.avgGrossWt}g</span>
                  </div>
                  <div className="avg-item">
                    <span>Avg Net: {item.avgNetWt}g</span>
                  </div>
                  <div className="avg-item">
                    <span>Avg Spec: {item.avgSpecWt}g</span>
                  </div>
                </div>
              )}

              {lotSizes.length > 1 && (
                <button
                  type="button"
                  className="remove-size-btn"
                  onClick={() => {
                    const newSizes = lotSizes.filter((_, i) => i !== index);
                    setLotSizes(newSizes);
                  }}
                  title="Remove this size"
                >
                  ×
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={() => {
              const availableSizes = getSizeOptions().filter(
                (size) => !lotSizes.some((item) => item.size === size),
              );

              if (availableSizes.length === 0) {
                alert("All available sizes have been added.");
                return;
              }

              setLotSizes([
                ...lotSizes,
                {
                  set: "",
                  size: "",
                  grossWt: "",
                  netWt: "",
                  avgGrossWt: "",
                  avgNetWt: "",
                  avgSpecWt: "",
                },
              ]);
            }}
            className="add-size-btn"
            disabled={lotSizes.length >= getSizeOptions().length}
          >
            + Add More Size
          </button>
        </div>

        {/* Totals Display */}
        <div className="totals-section">
          <h4>Lot Totals</h4>
          <div className="totals-grid">
            <div className="total-item">
              <span>Total Sets:</span>
              <strong>{totals.totalSets}</strong>
            </div>
            <div className="total-item">
              <span>Total Gross Wt:</span>
              <strong>{totals.totalGrossWt.toFixed(3)}g</strong>
            </div>
            <div className="total-item">
              <span>Total Net Wt:</span>
              <strong>{totals.totalNetWt.toFixed(3)}g</strong>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ========== MAIN RENDER ==========

  if (loading) {
    return (
      <div className="catlouge-container">
        <PageHeader title={currentConfig.title} />
        <div className="loading-container">
          <p>Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={currentConfig.title} />
      <div className="catlouge-container">
        {/* Filters Section */}
        <div className="filters">
          <div className="filter-group">
            <h3>Category</h3>
            <div className="horizontal-scroll-container">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-btn ${
                    selectedCategory === category ? "active" : ""
                  }`}
                  onClick={() => handleCategoryClick(category)}
                >
                  {category}
                  {categoryCounts[category] !== undefined && (
                    <span className="count-badge">
                      {categoryCounts[category]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3>Subcategory</h3>
            <div className="horizontal-scroll-container">
              {subcategories.map((subcategory) => (
                <button
                  key={subcategory}
                  className={`category-button ${
                    selectedSubcategory === subcategory ? "active" : ""
                  }`}
                  onClick={() => handleSubcategoryClick(subcategory)}
                >
                  {subcategory}
                  {subcategoryCounts[subcategory] !== undefined && (
                    <span className="count-badge">
                      {subcategoryCounts[subcategory]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Count */}
        <div className="product-count">
          <p>{products.length} Products found</p>
        </div>

        {/* Products Grid */}
        <div className="products-grid">
          {products.length === 0 ? (
            <div className="no-products">
              No {currentConfig.title.toLowerCase()} products found in{" "}
              {selectedCategory}-{selectedSubcategory}
            </div>
          ) : (
            products.map((product, index) => {
              const displayInfo = getProductDisplayInfo(product);

              return (
                <div
                  className="catalog-card"
                  key={`${product.id}-${index}`}
                  onClick={() => handleProductClick(product)}
                >
                  <div className="product-image">
                    {product.images && product.images.length > 0 && (
                      <img
                        src={product.images[0].url?.trim()}
                        alt={product.productName}
                        onError={(e) => {
                          e.target.src =
                            "https://via.placeholder.com/200x200?text=No+Image";
                        }}
                      />
                    )}
                    {(!product.images || product.images.length === 0) && (
                      <div className="no-image-placeholder">No Image</div>
                    )}

                    {displayInfo.isLot && <div className="lot-badge">LOT</div>}

                    <div
                      className="status-badge"
                      style={{
                        backgroundColor: currentConfig.badgeColor,
                        borderColor: currentConfig.badgeBorderColor,
                        color: currentConfig.badgeTextColor || "#1f2937",
                      }}
                    >
                      {currentConfig.badgeText}
                    </div>
                  </div>

                  <div className="product-info">
                    <h3 className="product-name">{product.productName}</h3>
                    {product.designCode && (
                      <p
                        className="product-category"
                        style={{
                          fontWeight: 600,
                          color: "var(--brand-500, #c49b27)",
                        }}
                      >
                        Design: {product.designCode}
                      </p>
                    )}
                    <p className="product-category">
                      {product.segment} / {product.category}
                    </p>
                    <p className="product-category">
                      {product.styleType} / {product.specification}
                    </p>

                    {displayInfo.isLot ? (
                      <>
                        <p className="product-weight-price">
                          {displayInfo.displayWeight} •{" "}
                          {displayInfo.displaySets}
                        </p>
                        <p className="product-sizes">
                          Sizes: {displayInfo.displaySizes}
                        </p>
                      </>
                    ) : (
                      <p className="product-weight-price">
                        {displayInfo.displayWeight}{" "}
                        {displayInfo.displaySets &&
                          `• ${displayInfo.displaySets}`}
                      </p>
                    )}

                    {!displayInfo.isLot && displayInfo.displaySizes && (
                      <p className="product-sizes">
                        Size: {displayInfo.displaySizes}
                      </p>
                    )}
                    {product.status === "rejected" &&
                      (product.rejectionReason || product.reason) && (
                        <p
                          className="card-rejection-reason"
                          style={{
                            margin: "6px 0",
                            padding: "4px 8px",
                            background: "#fff1f2",
                            border: "1px solid #fecdd3",
                            borderRadius: "6px",
                            color: "#be123c",
                            fontSize: "11px",
                            lineHeight: "1.3",
                          }}
                        >
                          <strong>Reason:</strong>{" "}
                          {product.rejectionReason || product.reason}
                        </p>
                      )}

                    {currentConfig.actionButtons &&
                      currentConfig.actionButtons.length > 0 && (
                        <div className="action-buttons">
                          {currentConfig.actionButtons.map(
                            (button, btnIndex) => (
                              <button
                                key={btnIndex}
                                className="action-btn"
                                style={{ backgroundColor: button.color }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (
                                    button.text === "Ready" ||
                                    button.text === "Edit"
                                  ) {
                                    button.onClick(product);
                                  } else {
                                    button.onClick(product.id);
                                  }
                                }}
                              >
                                {button.text}
                              </button>
                            ),
                          )}
                        </div>
                      )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Edit Drawer */}
        {drawerOpen && editingProduct && (
          <div className="lot-drawer-overlay" onClick={closeDrawer}>
            <div className="lot-drawer" onClick={(e) => e.stopPropagation()}>
              <div className="drawer-header">
                <h3>Edit Stock Details</h3>
                <button className="close-btn" onClick={closeDrawer}>
                  ×
                </button>
              </div>

              <div className="drawer-body">{renderLotDrawerContent()}</div>

              <div className="drawer-footer">
                <button className="cancel-btn" onClick={closeDrawer}>
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSaveData}
                  disabled={
                    lotSizes.length === 0 ||
                    !lotSizes.every(
                      (item) =>
                        item.size &&
                        item.set !== "" &&
                        item.set !== undefined &&
                        item.set !== "0" &&
                        item.grossWt !== "" &&
                        item.netWt !== "" &&
                        parseFloat(item.netWt) <= parseFloat(item.grossWt),
                    )
                  }
                >
                  Save & Mark as Ready
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Edit Rejected Product Modal */}
        {editRejectedModalOpen && selectedRejectedProduct && (
          <EditRejectedProductModal
            isOpen={editRejectedModalOpen}
            onClose={() => {
              setEditRejectedModalOpen(false);
              setSelectedRejectedProduct(null);
            }}
            product={selectedRejectedProduct}
            sellerProductData={sellerProductData}
            onProductUpdated={handleProductUpdated}
            onProductDeleted={handleProductDeletedFromModal}
          />
        )}
      </div>
    </>
  );
};

export default Catalogue;
